/**
 * DetailSurface TSL nodes. Builds, from a tiling MaterialX value-noise of world
 * position:
 *   - `detailNormal`: a [0,1]-encoded TANGENT-SPACE normal (flat = (0.5,0.5,1)).
 *     NOT a shading normal: three uses `normalNode` verbatim as the view-space
 *     normal, so DetailModifier decodes it and bends the material's own normal
 *     along the tangent frame (2026-09-22; before that the raw vector was the
 *     normal and every face shaded alike). The frame comes from (screen-space
 *     derivatives when the geometry has no tangents) — exactly a detail normal map.
 *   - `roughnessFactor`: a ~[-1,1] noise scalar the modifier centers + blends.
 * Uniforms are returned so the handle can live-tune them.
 *
 * Plain TSL chaining only — no nested array-destructured Fn (WGSL-codegen safe).
 */
import { positionWorld, mx_noise_vec3, mx_noise_float, vec3, uniform, float, smoothstep } from 'three/tsl';
import type { DetailSurfaceConfig } from './DetailConfig.js';

export interface DetailNodes {
  detailNormal: ReturnType<typeof vec3>;
  roughnessFactor: ReturnType<typeof mx_noise_float>;
  /** 0..1 — how much of the detail the pixel footprint resolves (1 when unfiltered). */
  keep: ReturnType<typeof float>;
  uniforms: { tiling: ReturnType<typeof uniform>; normalStrength: ReturnType<typeof uniform> };
}

export function createDetailNodes(config: DetailSurfaceConfig): DetailNodes {
  const tiling = uniform(config.tiling);
  const normalStrength = uniform(config.normalStrength);

  const p = positionWorld.mul(tiling);
  const n = mx_noise_vec3(p); // ~[-1,1] per component

  // FOOTPRINT FILTER (2026-09-15): the noise has ~one period per unit of `p`,
  // so the screen derivative of `p` is its periods-per-pixel. Fade the detail
  // out as that crosses the sampling limit — procedural noise has no mip
  // chain, and unfiltered it shimmers at distance and across a render-scale
  // change. Unfiltered: keep = 1 exactly, the graph is unchanged.
  const keep = config.filtered
    ? float(1).sub(smoothstep(float(config.filterStart), float(config.filterEnd), p.fwidth().length()))
    : float(1);
  const strength = normalStrength.mul(keep);

  // Encode a subtle tangent-space normal: flat is (0.5, 0.5, 1.0); xy deviate by
  // ±0.5*strength. DetailModifier decodes (×2−1) and applies it along the TBN.
  const nx = n.x.mul(strength).mul(0.5).add(0.5);
  const ny = n.y.mul(strength).mul(0.5).add(0.5);
  const detailNormal = vec3(nx, ny, 1.0);

  const roughnessFactor = mx_noise_float(p).mul(keep); // ~[-1,1]; modifier centers + scales

  return { detailNormal, roughnessFactor, keep, uniforms: { tiling, normalStrength } };
}
